import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { TaskCard } from "./TaskCard";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Edit, Trash2, Info, CheckSquare, Square } from "lucide-react";
import { format } from "date-fns";
import { useState } from "react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { EditTaskModal } from "./EditTaskModal";
import { useDeleteTask, useUpdateTask } from "@/hooks/tasks/useTasks";
import { TaskInfoPopover } from "./TaskInfoPopover";

interface TasksListProps {
  tasks: any[];
  onTaskUpdated?: () => void;
}

export const TasksList = ({ tasks, onTaskUpdated }: TasksListProps) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [deletingTask, setDeletingTask] = useState<any>(null);
  const [updatingTask, setUpdatingTask] = useState<string | null>(null);
  const deleteTaskMutation = useDeleteTask();
  const updateTaskMutation = useUpdateTask();

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      todo: { variant: "secondary" as const, label: t("tasks.status.todo", "To Do") },
      inProgress: { variant: "default" as const, label: t("tasks.status.inProgress", "In Progress") },
      done: { variant: "success" as const, label: t("tasks.status.done", "Done") },
      cancelled: { variant: "destructive" as const, label: t("tasks.status.cancelled", "Cancelled") }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.todo;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getPriorityBadge = (priority: string) => {
    const priorityConfig = {
      low: { variant: "secondary" as const, label: t("tasks.priority.low", "Low") },
      medium: { variant: "default" as const, label: t("tasks.priority.medium", "Medium") },
      high: { variant: "warning" as const, label: t("tasks.priority.high", "High") },
      urgent: { variant: "destructive" as const, label: t("tasks.priority.urgent", "Urgent") }
    };
    
    const config = priorityConfig[priority as keyof typeof priorityConfig] || priorityConfig.medium;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const handleEdit = (task: any) => {
    // Just update URL - let parent component handle the modal
    const currentParams = Object.fromEntries(searchParams.entries());
    setSearchParams({ ...currentParams, task: task.id }, { replace: false });
  };

  const handleDelete = (task: any) => {
    setDeletingTask(task);
  };

  const confirmDelete = async () => {
    if (deletingTask) {
      try {
        await deleteTaskMutation.mutateAsync(deletingTask.id);
        setDeletingTask(null);
        if (onTaskUpdated) {
          onTaskUpdated();
        }
      } catch (error) {
        console.error("Failed to delete task:", error);
      }
    }
  };

  // No longer needed - parent handles modal

  const handleToggleStatus = async (task: any) => {
    if (updatingTask === task.id) return;
    
    setUpdatingTask(task.id);
    try {
      const newStatus = task.status === 'done' ? 'todo' : 'done';
      
      await updateTaskMutation.mutateAsync({
        id: task.id,
        status: newStatus
      });

      // Update the task object locally
      task.status = newStatus;
      
      if (onTaskUpdated) {
        onTaskUpdated();
      }
    } catch (error) {
      console.error('Error updating task status:', error);
    } finally {
      setUpdatingTask(null);
    }
  };

  return (
    <>
      <div className="bg-white dark:bg-gray-800 rounded-sm shadow">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("tasks.list.name", "Name")}</TableHead>
              <TableHead>{t("tasks.list.status", "Status")}</TableHead>
              <TableHead>{t("tasks.list.priority", "Priority")}</TableHead>
              <TableHead>{t("tasks.list.dueDate", "Due Date")}</TableHead>
              <TableHead>{t("tasks.list.owner", "Owner")}</TableHead>
              <TableHead>{t("tasks.list.assignedTo", "Assigned To")}</TableHead>
              <TableHead>{t("tasks.list.linkedUsers", "Linked Users")}</TableHead>
              <TableHead>{t("tasks.list.actions", "Actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tasks.map((task) => (
                          <TableRow key={task.id} className={task.due_date && new Date(task.due_date) < new Date() && task.status !== 'done' ? 'bg-red-50 dark:bg-red-900/20' : ''}>
              <TableCell className="font-medium">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggleStatus(task)}
                    disabled={updatingTask === task.id}
                    className={`p-1 rounded transition-colors ${
                      task.status === 'done' 
                        ? 'text-green-600 hover:text-green-700' 
                        : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                    } ${updatingTask === task.id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    title={task.status === 'done' ? t("tasks.actions.markUndone", "Mark as undone") : t("tasks.actions.markDone", "Mark as done")}
                  >
                    {task.status === 'done' ? (
                      <CheckSquare className="h-4 w-4" />
                    ) : (
                      <Square className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    onClick={() => handleEdit(task)}
                    className={`text-left hover:underline ${(task.status === 'done' || task.status === 'cancelled') ? 'line-through text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}`}
                    title={t("common.edit", "Edit task")}
                  >
                    {task.name}
                  </button>
                </div>
              </TableCell>
              <TableCell>{getStatusBadge(task.status)}</TableCell>
              <TableCell>{getPriorityBadge(task.priority)}</TableCell>
              <TableCell>
                {task.due_date ? (
                  <span className={task.due_date && new Date(task.due_date) < new Date() && task.status !== 'done' ? 'text-red-600 font-medium' : ''}>
                    {format(new Date(task.due_date), "MMM dd, yyyy")}
                  </span>
                ) : "-"}
              </TableCell>
              <TableCell>
                {task.owner ? `${task.owner.first_name} ${task.owner.last_name}` : "-"}
              </TableCell>
              <TableCell>
                {task.assignee ? `${task.assignee.first_name} ${task.assignee.last_name}` : "-"}
              </TableCell>
              <TableCell>
                {task.linked_users && task.linked_users.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {task.linked_users.slice(0, 2).map((user, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        {user.first_name} {user.last_name}
                      </Badge>
                    ))}
                    {task.linked_users.length > 2 && (
                      <Badge variant="outline" className="text-xs">
                        +{task.linked_users.length - 2}
                      </Badge>
                    )}
                  </div>
                ) : "-"}
              </TableCell>
              <TableCell>
                  <div className="flex items-center gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          title={t("tasks.info", "Task Information")}
                        >
                          <Info className="h-4 w-4" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80 p-0 max-w-[20rem]" align="end">
                        <TaskInfoPopover task={task} onTaskUpdated={onTaskUpdated} />
                      </PopoverContent>
                    </Popover>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleEdit(task)}
                      title={t("common.edit", "Edit")}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleDelete(task)}
                      title={t("common.delete", "Delete")}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deletingTask} onOpenChange={(open) => !open && setDeletingTask(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("common.deleteConfirmation", { count: 1 })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("common.deleteWarning")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteTaskMutation.isPending}
            >
              {deleteTaskMutation.isPending ? t("common.deleting", "Deleting...") : t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
